import { Exchange } from '../../proto/generated/common'
import { SecurityCategory_CategoryCode } from '../../proto/generated/security'
import { InstrumentLabels } from '../../util/cashInstrument'
import { useDispatch } from 'react-redux'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import commonActions from '../../actions/common.actions'
import { IconButton, ListItem, ListItemButton, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ExtraSmallButtonHoverable } from '../custom'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { getRowButtonSx } from './rowStyles'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import ThinArrowUpIcon from '../../assets/icons/ThinArrowUpIcon'
import PlusIcon from '../../assets/icons/PlusIcon'
import DeleteIcon from '../../assets/icons/DeleteIcon'
import { darkHoverBtnColor, lightHoverBtnColor } from '../../theme/theme'
import SecurityRowCells from './SecurityRowCells'

interface SecurityRowProps {
    symbol: string
    series?: string
    displayName: string
    exchange?: Exchange
    /** Show an add / remove control at the end of the row (watchlist picker). */
    tailIcon?: boolean
    deleteIcon?: boolean
    categoryCode: SecurityCategory_CategoryCode
    instrument?: string
    /** instrument -> display name from the categories response ("Stock", "ETF", "Stock Future"). */
    instrumentLabels?: InstrumentLabels
    highlightSelected?: boolean
    logo?: ReactNode
    logoUrl?: string
    selected?: boolean
    expression?: string
    onSecurityDeleted?: any
    isExpression?: boolean
    colorSymbol?: string
    searchTerm: string
    focused: boolean
    onSecuritySelect?: any
    expressionMode: boolean
    /** Row has related securities (other exchange listing, near future, ATM options) that can be shown beneath it. */
    expandable?: boolean
    expanded?: boolean
    onToggleExpand?: () => void
    /** Render as a related (child) row: indented, no logo. */
    indent?: boolean
    /** Exchanges to show as stacked logos after the label (defaults to the row's own exchange). */
    exchanges?: Exchange[]
    /** Rolling near-month future (X_M1): shown TradingView-style as "SYMBOL1!" with a ROLLING tag. */
    rolling?: boolean
}

/** Same hover treatment as ExtraSmallButtonHoverable. */
const hoverSx = {
    '&:hover, &:active': {
        color: 'text.primary',
        backgroundColor: (theme: { palette: { mode: string } }) => alpha(theme.palette.mode === 'light' ? lightHoverBtnColor : darkHoverBtnColor, 0.8)
    }
}

const SecurityRow = ({
    symbol,
    series,
    displayName,
    exchange,
    categoryCode,
    instrument,
    instrumentLabels,
    logo,
    logoUrl,
    selected: itemSelected,
    searchTerm,
    colorSymbol,
    onSecuritySelect,
    onSecurityDeleted,
    isExpression = false,
    highlightSelected = false,
    focused,
    tailIcon = false,
    deleteIcon = false,
    expressionMode,
    expandable = false,
    expanded = false,
    onToggleExpand,
    indent = false,
    exchanges,
    rolling = false
}: SecurityRowProps) => {
    const dispatch = useDispatch()
    const [hovered, setHovered] = useState(false)
    const targetRef = useRef<HTMLLIElement>(null)
    const [selected, setSelected] = useState<boolean>(false)

    const handleDeleteClick = useCallback(
        (e: any) => {
            if (!onSecurityDeleted) {
                commonActions.setApiError(dispatch, { error: true, message: 'Something went wrong', errorType: 'error' })
                return
            }
            onSecurityDeleted()
            e.stopPropagation()
        },
        [onSecurityDeleted, dispatch]
    )

    useEffect(() => {
        if (focused && targetRef.current) {
            targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
        }
        if (focused) {
            onSecuritySelect(selected, focused)
        }
    }, [focused])

    useEffect(() => {
        setSelected(itemSelected ?? false)
    }, [itemSelected])

    const showTail = tailIcon || (isExpression && deleteIcon)
    // Trailing controls: the related-securities chevron (slot always reserved so exchange columns line up),
    // then the watchlist add / remove or expression delete, visible on hover.
    const trailingEnd = (
        <>
            <IconButton
                size={'small'}
                aria-label={expanded ? 'Hide related' : 'Show related'}
                tabIndex={-1}
                sx={{ p: 0, width: 22, height: 22, flexShrink: 0, borderRadius: '4px', color: 'text.secondary', visibility: expandable ? 'visible' : 'hidden', ...hoverSx }}
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onToggleExpand?.()
                }}
            >
                {expanded ? <ThinArrowUpIcon sx={{ fontSize: 18 }} /> : <ThinArrowDownIcon sx={{ fontSize: 18 }} />}
            </IconButton>
            {showTail && (
                <Tooltip title={isExpression && deleteIcon ? 'Delete' : selected ? 'Remove from watchlist' : 'Add to watchlist'} arrow>
                    <ExtraSmallButtonHoverable
                        variant={'outlined'}
                        color={'inherit'}
                        size={'small'}
                        // same footprint as the chevron so the row keeps its 24px content height
                        sx={{ maxWidth: 22, minWidth: 22, height: '22px !important', padding: 0, flexShrink: 0, visibility: hovered || focused ? 'visible' : 'hidden' }}
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            if (isExpression && deleteIcon) {
                                handleDeleteClick(e)
                                return
                            }
                            !expressionMode && setSelected(!selected)
                            onSecuritySelect(!selected, false)
                        }}
                    >
                        {(isExpression && deleteIcon) || selected ? <DeleteIcon sx={{ fontSize: 15 }} /> : <PlusIcon sx={{ fontSize: 16 }} />}
                    </ExtraSmallButtonHoverable>
                </Tooltip>
            )}
        </>
    )

    return (
        <ListItem
            disablePadding
            className={indent ? 'security-item security-item-related' : 'security-item'}
            sx={{
                borderBottom: 0.5,
                borderColor: 'divider',
                // the expanded row and its related rows share a faint tint so the group reads as one
                ...(indent || expanded ? { bgcolor: (theme) => alpha(theme.palette.text.primary, 0.025) } : {})
            }}
            onClick={() => {
                AnalyticsManager.logEvent('Symbol Clicked', { source: 'symbol-search', symbol: symbol })
                !expressionMode && setSelected(!selected)
                onSecuritySelect(!selected, false)
            }}
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
            ref={targetRef}
        >
            <ListItemButton disableGutters={true} sx={getRowButtonSx(focused)}>
                <SecurityRowCells
                    symbol={symbol}
                    series={series}
                    displayName={displayName}
                    exchange={exchange}
                    exchanges={exchanges}
                    instrument={instrument}
                    instrumentLabels={instrumentLabels}
                    rolling={rolling}
                    logo={logo}
                    logoUrl={logoUrl}
                    colorSymbol={colorSymbol}
                    searchTerm={searchTerm}
                    indent={indent}
                    selected={highlightSelected && selected}
                    trailingEnd={trailingEnd}
                />
            </ListItemButton>
        </ListItem>
    )
}

export default SecurityRow
